/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, OnInit} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {FabMessageService} from '@fabric/components/message';
import {
  ColDef, PostSortRowsParams,
  RowSelectionOptions,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy
} from 'ag-grid-community';
import {DISMISS_MSG} from '../../../../common/constants';
import {DeletePopUpComponent} from '../../../../core/components/pop-ups/delete-pop-up/delete-pop-up.component';
import {AutocompleteFilterComponent} from '../../../../core/filters/autocomplete-filter/autocomplete-filter.component';
import {StateService} from '../../../../core/state/state.service';
import {GlobalVehicleScenarioMap} from '../../../../models/global-vehicle-scenario-map.model';
import {PropulsionScenario} from '../../../../models/propulsion-scenario-dto-model';
import {VehicleScenario} from '../../../../models/vehicle-scenario-dto.model';
import {PropulsionScenarioService} from '../../../../services/propulsion-scenario-service';
import {VehicleScenarioService} from '../../../../services/vehicle-scenario.service';
import {
  EditPropulsionScenarioPopupComponent
} from './edit-propulsion-scenario-popup/edit-propulsion-scenario-popup.component';

@Component({
    selector: 'app-propulsion-scenario',
    templateUrl: './propulsion-scenario.component.html',
    styleUrls: ['./propulsion-scenario.component.scss'],
    standalone: false
})
export class PropulsionScenarioComponent implements OnInit {

  deleteMessage: string;
  showSpinner: boolean;
  noData: boolean;
  existsInVehicleScenario: boolean;
  pageSize: number;
  scenarioHeaders: ColDef[] = [
    {field: 'id', headerName: 'ID', filter: AutocompleteFilterComponent},
    {field: 'propulsionScenarioName', headerName: 'Name', filter: AutocompleteFilterComponent},
    {field: 'forecastOrganization', headerName: 'FCORG', filter: AutocompleteFilterComponent},
    {field: 'isDeleteLocked', headerName: 'Delete Lock', filter: AutocompleteFilterComponent},
    {field: 'isEditLocked', headerName: 'Edit Lock', filter: AutocompleteFilterComponent},
    {field: 'isHidden', headerName: 'Hidden', filter: AutocompleteFilterComponent},
    {field: 'createdBy', headerName: 'Created By', filter: AutocompleteFilterComponent},
    {field: 'propulsionScenarioAssumption', headerName: 'Assumption', filter: AutocompleteFilterComponent}
  ];
  propulsionScenarios: PropulsionScenario[] = [];
  vehicleScenarios: VehicleScenario[] = [];
  globalScenarios: GlobalVehicleScenarioMap[] = [];
  selectedValue: any;
  selectedId: number;

  rowSelection: RowSelectionOptions = {
    mode: 'singleRow',
    checkboxes: false,
    enableClickSelection: true,
    enableSelectionWithoutKeys: true,
  };

  autoSizeStrategy:
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy = {
    type: "fitCellContents",
  };

  postSortRows: (params: PostSortRowsParams) => void = (
    params: PostSortRowsParams<PropulsionScenario>,
  ) => {
    // Only post sort rows if the user has a primary FCORG preference
    if (this.stateService.userPreferences?.some(pref => pref.preferenceKey === 'Primary FCORG')) {
      const rowNodes = params.nodes;
      // here we put the Primary FCORG rows on top while preserving the sort order if the user has a primary FCORG preference
      let nextInsertPos = 0;
      for (let i = 0; i < rowNodes.length; i++) {
        const fcorg = rowNodes[i].data ? rowNodes[i].data!.forecastOrganization : undefined;
        if (fcorg === this.stateService.userPreferences.find(pref => pref.preferenceKey === 'Primary FCORG')?.preferenceValue) {
          rowNodes.splice(nextInsertPos, 0, rowNodes.splice(i, 1)[0]);
          nextInsertPos++;
        }
      }
    }
  };


  constructor(public snackBar: FabMessageService,
              private readonly propulsionScenarioService: PropulsionScenarioService,
              private readonly vehicleScenarioService: VehicleScenarioService,
              private readonly stateService: StateService,
              private readonly dialog: MatDialog) {
  }

  ngOnInit(): void {
    this.selectedValue = null;
    this.loadPropulsionScenarios();
    this.pageSize = 20;
  }


  /**
   * Fetch all propulsion scenarios from backend endpoint
   */
  loadPropulsionScenarios(): void {
    this.showSpinner = true;
    this.propulsionScenarioService.getPropulsionScenarios(this.stateService.getUserGmid()).subscribe(
      data => {
        this.propulsionScenarios = data;
      },
      err => {
        this.errorHandle(err);
      },
      () => {
        this.showSpinner = false;
        this.noData = (this.propulsionScenarios.length === 0);
      }
    );
  }

  /**
   * Delete a selected propulsion Scenario from DB.
   */
  deletePropulsionScenario(): void {
    this.showSpinner = true;
    const propulsionScenario: PropulsionScenario = this.propulsionScenarios.find(is => is.id === this.selectedId);
    this.propulsionScenarioService.getGlobalVehicleScenarioMapByPropScenId(this.selectedId).subscribe(
      data => {
        this.globalScenarios = data;
      },
      err => {
        this.errorHandle(err);
        this.showSpinner = false;
      },
      () => {
        if (this.globalScenarios.length > 0) {
          this.snackBar.displayError('The selected Propulsion Scenario is attached to Global ' +
            'Vehicle Scenarios therefore, it cannot be deleted', DISMISS_MSG);
        } else if (!propulsionScenario.isDeleteLocked) {
          this.vehicleScenarioService.getVehicleScenarioByPropScenId(this.selectedId).subscribe(
            data => {
              this.vehicleScenarios = data;
            },
            err => {
              this.errorHandle(err);
              this.showSpinner = false;
            },
            () => {
              if (this.vehicleScenarios.length > 0) {
                this.existsInVehicleScenario = true;
                this.deleteMessage = 'The selected Propulsion Scenario is attached to one or more Vehicle Scenarios. ' +
                  'Would you still like to proceed with delete?';
              } else {
                this.existsInVehicleScenario = false;
                this.deleteMessage = 'Delete';
              }
              const dialogRef = this.dialog.open(DeletePopUpComponent, {
                data: {
                  title: this.deleteMessage,
                  valueToDelete: this.selectedValue
                }
              });
              dialogRef.afterClosed().subscribe(res => {
                // received data from dialog-component
                if (res && res.data.deleteValue) {

                  this.propulsionScenarioService.deletePropulsionScenario(this.selectedId, this.existsInVehicleScenario).subscribe({
                    error: err => {
                      this.errorHandle(err);
                      this.loadPropulsionScenarios();
                      this.showSpinner = false;
                    },
                    complete: () => {
                      this.snackBar.displaySuccess('Successfully removed propulsion scenario', DISMISS_MSG);
                      this.loadPropulsionScenarios();
                      this.selectedValue = null;
                    }
                  });
                }
              });
            }
          );
        } else {
          this.snackBar.displayError('The selected Propulsion Scenario is locked and can not be deleted', DISMISS_MSG);
          this.loadPropulsionScenarios();
          this.selectedValue = null;
        }
        this.showSpinner = false;
      }
    );
  }


  /**
   * Opens the Edit Propulsion Scenario Popup
   */
  editPropulsionScenario(id: number): void {
    const propulsionScenario = this.propulsionScenarios.find(is => is.id === id);
    this.dialog.open(EditPropulsionScenarioPopupComponent, {
      disableClose: true,
      width: '500px',
      data: propulsionScenario
    }).afterClosed().subscribe({
      complete: () => {
        this.loadPropulsionScenarios();
        this.selectedValue = null;
      }
    });
  }

  /**
   * Event for selecting a scenario
   * @param event the event object
   */
  onSelect(event) {
    if (event.node.selected) {
      this.selectedValue = (event.data.propulsionScenarioName);
      this.selectedId = event.data.id;
    } else if (event.data.id === this.selectedId) {
      this.selectedValue = null;
      this.selectedId = null;
    }
  }

  /**
   * display message on error.
   * @param error custom error message.
   */
  errorHandle(error: string): void {
    this.showSpinner = false;
    this.selectedValue = null;
    this.snackBar.displayError(error, DISMISS_MSG);
  }
}
